import { Link } from "react-router-dom";
import { ArrowRight, Sun, Battery, Droplets, Leaf } from "lucide-react";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";

const CARDS = [
  {
    icon: Sun,
    title: "Use your own solar",
    body: "Run your hot water cycle during the day to soak up surplus solar generation instead of selling it back at a low rate.",
    img: "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1200&q=80",
    alt: "Rooftop solar panels at sunrise",
  },
  {
    icon: Battery,
    title: "Battery-ready",
    body: "Hot water acts like thermal storage — pairing naturally with home batteries and softening peak demand.",
    img: "https://plus.unsplash.com/premium_photo-1764687973550-c414ea83f76d?auto=format&fit=crop&w=1200&q=80",
    alt: "Modern home with solar panels and a wall battery for energy storage",
  },
  {
    icon: Droplets,
    title: "Smarter scheduling",
    body: "Free Wi-Fi app control lets you schedule heat-up cycles to match your solar window and lower running costs.",
    img: "https://images.unsplash.com/photo-1519558260268-cde7e03a0152?auto=format&fit=crop&w=1200&q=80",
    alt: "Smart-home device and smartphone app for scheduling",
  },
  {
    icon: Leaf,
    title: "Lower carbon, lower bills",
    body: "Clean electricity plus heat-pump efficiency = the cheapest, lowest-emission hot water you'll ever run at home.",
    img: "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=1200&q=80",
    alt: "Lush green mountain landscape",
  },
];

export const SolarHotWaterSection = () => {
  return (
    <section data-testid="solar-hot-water" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="max-w-3xl">
          <SectionLabel>Solar Hot Water</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold tracking-tight text-[#0B132B] leading-[1.05]">
            A perfect pair: <span className="text-[#10B981]">hot water and solar.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            Electric hot water heat pumps are one of the best appliances to pair with a solar or electrified home — they shift, store and slash your daily energy bill.
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CARDS.map((c, i) => (
            <Reveal key={i} delay={i * 80}>
              <Card
                data-testid={`solar-card-${i}`}
                className="group h-full overflow-hidden rounded-2xl border-[#E2E8F0] bg-white hover:border-[#10B981]/40 hover:shadow-xl hover:shadow-emerald-500/8 hover:-translate-y-1 transition-all duration-300"
              >
                {/* Visual */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                  />
                  {/* Brand tint overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/60 via-[#0B132B]/10 to-transparent" />
                  {/* Icon chip */}
                  <span className="absolute top-3 left-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/95 backdrop-blur text-[#10B981] shadow-sm">
                    <c.icon className="h-4 w-4" strokeWidth={2} />
                  </span>
                </div>

                {/* Body */}
                <div className="p-5">
                  <p className="font-heading text-[17px] font-semibold text-[#0B132B] leading-tight">{c.title}</p>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{c.body}</p>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>

        {/* Coming-next card */}
        <Reveal className="mt-12">
          <Card className="rounded-3xl p-8 border-[#E2E8F0] bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-[240px] w-[240px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
            <div className="relative grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#10B981] text-white shrink-0">
                    <Sun className="h-5 w-5" />
                  </span>
                  <p className="font-heading text-xs uppercase tracking-widest text-[#475569] font-semibold">Coming next</p>
                </div>
                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B132B] leading-tight">
                  Solar PV + Battery, the AeroMax way.
                </h3>
                <p className="mt-2 text-[#475569] leading-relaxed text-sm sm:text-base">
                  Hot water is just the first step. AeroMax Energy is building toward full home electrification — solar PV, home battery storage, and smart whole-home energy control.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link
                  to="/solar-battery"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] hover:bg-[#050914] text-white px-5 py-3 font-semibold text-sm transition-all"
                >
                  Register Interest <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </Card>
        </Reveal>
      </div>
    </section>
  );
};
